import React, { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { CrewMember } from "../@types";
import { useGetCrewMembersMutation } from "../driverApiSlice";

export interface CrewOption {
  id: string;
  name: string;
  email: string;
}

interface CrewDropdownMenuProps {
  value?: string;
  onChange?: (value: CrewOption | null) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  label?: string;
  error?: string;
}

const CrewDropdownMenu: React.FC<CrewDropdownMenuProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Crew member",
  name = "crew-member",
  id = "crew-member",
  showLabel = true,
  error,
  label,
}) => {
  const [getCrews, { data: crewData, isLoading, isError, error: errorData }] =
    useGetCrewMembersMutation();

  const partners = useMemo(() => {
    return crewData?.data.data || [];
  }, [crewData?.data.data]);

  useEffect(() => {
    getCrews({
      pageNumber: 0,
      pageSize: 100,
    });
  }, [getCrews]);

  const driversOptions: CrewOption[] = useMemo(() => {
    if (isLoading) {
      return [];
    }

    if (isError) {
      return [];
    }

    return partners.map((crewMember: CrewMember) => ({
      id: crewMember.id,
      name: `${crewMember.fullName} -- ${crewMember.department}`,
      email: crewMember.email,
    }));
  }, [partners, isLoading, isError]);

  const handleValueChange = (option: CrewOption) => {
    onChange?.(option);
  };

  // Helper functions for MySelect
  const getValue = (option: CrewOption) => option.id;
  const getName = (option: CrewOption) => option.name;

  return (
    <div className={cn("w-full")}>
      <MySelect<CrewOption>
        name={name}
        id={id}
        label={showLabel ? label || "Crew member" : ""}
        options={driversOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={(id) => {
          const selectedOption = driversOptions.find(
            (option) => option.id === id
          );
          onChange?.(selectedOption || null);
        }}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading partners"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default CrewDropdownMenu;
